import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { toast } from 'sonner';
import { clubsAPI, membershipsAPI, playersAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  CreditCard,
  Users,
  Bell,
  Plus,
  ChevronLeft,
  IndianRupee,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Clock,
  Trash2,
  Edit,
  Send,
  Wallet,
  Power,
  Ban,
} from 'lucide-react';

export default function ClubMembership() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [club, setClub] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [activeTab, setActiveTab] = useState('tiers');
  
  // Dialog states
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [membershipSheetOpen, setMembershipSheetOpen] = useState(false);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [editingTier, setEditingTier] = useState(null);
  
  // Form states
  const [tierForm, setTierForm] = useState({
    name: '',
    fee_amount: '',
    fee_currency: 'INR',
    duration_days: 30,
    benefits: '',
    max_members: '',
  });
  
  const [membershipForm, setMembershipForm] = useState({
    user_id: '',
    tier_id: '',
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    notes: '',
  });

  const fetchData = async () => {
    try {
      const [clubRes, tiersRes, membershipsRes, remindersRes, paymentsRes, playersRes] = await Promise.all([
        clubsAPI.getById(clubId),
        membershipsAPI.getTiers(clubId),
        membershipsAPI.getByClub(clubId),
        membershipsAPI.getReminders(clubId),
        membershipsAPI.getPayments(clubId),
        playersAPI.getAll(),
      ]);
      
      setClub(clubRes.data);
      setTiers(tiersRes.data || []);
      setMemberships(membershipsRes.data || []);
      setReminders(remindersRes.data || []);
      setPayments(paymentsRes.data || []);
      setPlayers(playersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clubId]);

  const handleCreateTier = async () => {
    if (!tierForm.name || !tierForm.fee_amount) {
      toast.error('Name and fee amount are required');
      return;
    }

    try {
      const data = {
        name: tierForm.name,
        fee_amount: parseFloat(tierForm.fee_amount),
        fee_currency: tierForm.fee_currency,
        duration_days: parseInt(tierForm.duration_days),
        benefits: tierForm.benefits ? tierForm.benefits.split(',').map(b => b.trim()) : [],
        max_members: tierForm.max_members ? parseInt(tierForm.max_members) : null,
      };

      if (editingTier) {
        await membershipsAPI.updateTier(editingTier.tier_id, data);
        toast.success('Tier updated');
      } else {
        await membershipsAPI.createTier(clubId, data);
        toast.success('Tier created');
      }
      
      setTierDialogOpen(false);
      setEditingTier(null);
      setTierForm({ name: '', fee_amount: '', fee_currency: 'INR', duration_days: 30, benefits: '', max_members: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tier');
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm('Delete this tier?')) return;
    
    try {
      await membershipsAPI.deleteTier(tierId);
      toast.success('Tier deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  const handleToggleTier = async (tierId) => {
    try {
      const res = await membershipsAPI.toggleTier(tierId);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle tier');
    }
  };

  const handleToggleMember = async (membershipId) => {
    try {
      const res = await membershipsAPI.toggleMember(membershipId);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle membership');
    }
  };

  const handleCreateMembership = async () => {
    if (!membershipForm.user_id || !membershipForm.tier_id) {
      toast.error('Select a member and tier');
      return;
    }

    try {
      await membershipsAPI.create({
        club_id: clubId,
        user_id: membershipForm.user_id,
        tier_id: membershipForm.tier_id,
      });
      toast.success('Membership created');
      setMembershipSheetOpen(false);
      setMembershipForm({ user_id: '', tier_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create membership');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || !selectedMembership) {
      toast.error('Enter payment amount');
      return;
    }

    try {
      await membershipsAPI.recordPayment({
        membership_id: selectedMembership.membership_id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes || null,
      });
      toast.success('Payment recorded');
      setPaymentSheetOpen(false);
      setSelectedMembership(null);
      setPaymentForm({ amount: '', payment_method: 'cash', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleSendReminder = async (membershipId) => {
    try {
      await membershipsAPI.sendReminder(membershipId);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const openEditTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      name: tier.name,
      fee_amount: tier.fee_amount.toString(),
      fee_currency: tier.fee_currency,
      duration_days: tier.duration_days,
      benefits: tier.benefits.join(', '),
      max_members: tier.max_members?.toString() || '',
    });
    setTierDialogOpen(true);
  };

  const openPaymentSheet = (membership) => {
    setSelectedMembership(membership);
    setPaymentForm({ amount: '', payment_method: 'cash', notes: '' });
    setPaymentSheetOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'expired': return 'bg-gray-500';
      case 'overdue': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      case 'overdue': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 max-w-2xl mx-auto" data-testid="club-membership-page">
        {/* Header - Mobile Optimized */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clubs')} className="shrink-0">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold truncate">{club?.name}</h1>
            <p className="text-sm text-muted-foreground">Membership Management</p>
          </div>
        </div>

        {/* Tabs - Mobile Optimized */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-auto">
            <TabsTrigger value="tiers" className="text-xs sm:text-sm py-2 px-1">
              <CreditCard className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Tiers</span>
            </TabsTrigger>
            <TabsTrigger value="members" className="text-xs sm:text-sm py-2 px-1">
              <Users className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Members</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-xs sm:text-sm py-2 px-1">
              <Wallet className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Payments</span>
            </TabsTrigger>
          </TabsList>

          {/* Tiers Tab */}
          <TabsContent value="tiers" className="mt-4 space-y-4">
            <Button 
              className="w-full bg-secondary hover:bg-secondary/90" 
              onClick={() => { setEditingTier(null); setTierForm({ name: '', fee_amount: '', fee_currency: 'INR', duration_days: 30, benefits: '', max_members: '' }); setTierDialogOpen(true); }}
              data-testid="add-tier-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Membership Tier
            </Button>

            {tiers.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <CreditCard className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">No tiers yet. Create your first tier.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tiers.map((tier) => (
                  <Card key={tier.tier_id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-lg">{tier.name}</h3>
                          <div className="flex items-center gap-1 text-2xl font-heading font-bold text-primary mt-1">
                            <IndianRupee className="h-5 w-5" />
                            {tier.fee_amount}
                            <span className="text-sm font-normal text-muted-foreground">/{tier.duration_days} days</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleToggleTier(tier.tier_id)} title={tier.enabled === false ? 'Enable' : 'Disable'} data-testid={`toggle-tier-${tier.tier_id}`}>
                            <Power className={`h-4 w-4 ${tier.enabled === false ? 'text-muted-foreground' : 'text-green-500'}`} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditTier(tier)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteTier(tier.tier_id)} className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {tier.benefits?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {tier.benefits.map((benefit) => (
                            <Badge key={`${tier.tier_id || tier.name}-${benefit}`} variant="outline" className="text-xs">{benefit}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                        <span>{tier.active_members} active members</span>
                        {tier.max_members && <span>Max: {tier.max_members}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="mt-4 space-y-4">
            <Button 
              className="w-full bg-secondary hover:bg-secondary/90" 
              onClick={() => setMembershipSheetOpen(true)}
              data-testid="add-member-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Member
            </Button>

            {memberships.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">No members yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {memberships.map((membership) => {
                  const member = players.find(p => p.user_id === membership.user_id);
                  const tier = tiers.find(t => t.tier_id === membership.tier_id);
                  const amountDue = (membership.amount_due || tier?.fee_amount || 0) - (membership.amount_paid || 0);
                  
                  return (
                    <Card key={membership.membership_id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium truncate">{member?.name || 'Unknown'}</h3>
                              <Badge className={`${getStatusColor(membership.status)} text-xs`}>
                                {membership.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{tier?.name || 'Unknown tier'}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className={getPaymentStatusColor(membership.payment_status)}>
                                {membership.payment_status === 'paid' ? (
                                  <CheckCircle className="h-4 w-4 inline mr-1" />
                                ) : (
                                  <Clock className="h-4 w-4 inline mr-1" />
                                )}
                                {membership.payment_status}
                              </span>
                              {amountDue > 0 && (
                                <span className="text-muted-foreground">
                                  Due: ₹{amountDue}
                                </span>
                              )}
                            </div>
                          </div>
                          {amountDue > 0 && (
                            <Button 
                              size="sm" 
                              onClick={() => openPaymentSheet(membership)}
                              className="shrink-0"
                            >
                              <Wallet className="h-4 w-4 mr-1" />
                              Collect
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleToggleMember(membership.membership_id)}
                            title={membership.status === 'disabled' ? 'Enable' : 'Disable'}
                            data-testid={`toggle-member-${membership.membership_id}`}
                          >
                            {membership.status === 'disabled' ? <Power className="h-4 w-4 text-muted-foreground" /> : <Ban className="h-4 w-4 text-red-400" />}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders" className="mt-4 space-y-4">
            {reminders.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
                  <p className="text-muted-foreground text-sm">All payments are up to date!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reminders.map((reminder) => (
                  <Card key={reminder.membership_id} className={reminder.is_overdue ? 'border-red-500/50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium truncate">{reminder.user_name}</h3>
                            {reminder.is_overdue && (
                              <Badge className="bg-red-500 text-xs">Overdue</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{reminder.tier_name}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-lg font-bold">₹{reminder.amount_due}</span>
                            <span className={`text-sm ${reminder.is_overdue ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {reminder.is_overdue 
                                ? `${Math.abs(reminder.days_until_due)} days overdue`
                                : `Due in ${reminder.days_until_due} days`
                              }
                            </span>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleSendReminder(reminder.membership_id)}
                          className="shrink-0"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-4 space-y-4">
            {payments.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Wallet className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">No payments recorded yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {payments.slice(0, 20).map((payment) => (
                  <Card key={payment.payment_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{payment.user_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {payment.payment_method} • {formatDate(payment.created_at)}
                          </p>
                        </div>
                        <span className="text-lg font-bold text-green-500">+₹{payment.amount}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Tier Dialog */}
        <Dialog open={tierDialogOpen} onOpenChange={setTierDialogOpen}>
          <DialogContent className="max-w-sm mx-auto">
            <DialogHeader>
              <DialogTitle>{editingTier ? 'Edit Tier' : 'Create Tier'}</DialogTitle>
              <DialogDescription>
                {editingTier ? 'Update membership tier details' : 'Define a new membership tier'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tier Name *</Label>
                <Input
                  placeholder="e.g., Gold, Silver, Basic"
                  value={tierForm.name}
                  onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                  data-testid="tier-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fee Amount *</Label>
                  <Input
                    type="number"
                    placeholder="500"
                    value={tierForm.fee_amount}
                    onChange={(e) => setTierForm({ ...tierForm, fee_amount: e.target.value })}
                    data-testid="tier-fee-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (days)</Label>
                  <Select value={tierForm.duration_days.toString()} onValueChange={(v) => setTierForm({ ...tierForm, duration_days: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">Monthly</SelectItem>
                      <SelectItem value="90">Quarterly</SelectItem>
                      <SelectItem value="180">Half-Yearly</SelectItem>
                      <SelectItem value="365">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Benefits (comma separated)</Label>
                <Textarea
                  placeholder="Court access, Equipment rental, Coaching"
                  value={tierForm.benefits}
                  onChange={(e) => setTierForm({ ...tierForm, benefits: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Members (optional)</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={tierForm.max_members}
                  onChange={(e) => setTierForm({ ...tierForm, max_members: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTierDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTier} className="bg-secondary hover:bg-secondary/90" data-testid="save-tier-btn">
                {editingTier ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Member Sheet */}
        <Sheet open={membershipSheetOpen} onOpenChange={setMembershipSheetOpen}>
          <SheetContent side="bottom" className="h-auto max-h-[80vh]">
            <SheetHeader>
              <SheetTitle>Add Member</SheetTitle>
              <SheetDescription>Assign a membership to a player</SheetDescription>
            </SheetHeader>
            <div className="space-y-4 py-6">
              <div className="space-y-2">
                <Label>Select Player</Label>
                <Select value={membershipForm.user_id} onValueChange={(v) => setMembershipForm({ ...membershipForm, user_id: v })}>
                  <SelectTrigger data-testid="member-select">
                    <SelectValue placeholder="Choose a player" />
                  </SelectTrigger>
                  <SelectContent>
                    {players.filter(p => !memberships.some(m => m.user_id === p.user_id && m.status === 'active')).map((player) => (
                      <SelectItem key={player.user_id} value={player.user_id}>
                        {player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Select Tier</Label>
                <Select value={membershipForm.tier_id} onValueChange={(v) => setMembershipForm({ ...membershipForm, tier_id: v })}>
                  <SelectTrigger data-testid="tier-select">
                    <SelectValue placeholder="Choose a tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map((tier) => (
                      <SelectItem key={tier.tier_id} value={tier.tier_id}>
                        {tier.name} - ₹{tier.fee_amount}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-secondary hover:bg-secondary/90" onClick={handleCreateMembership} data-testid="confirm-member-btn">
                Add Member
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Payment Sheet */}
        <Sheet open={paymentSheetOpen} onOpenChange={setPaymentSheetOpen}>
          <SheetContent side="bottom" className="h-auto max-h-[80vh]">
            <SheetHeader>
              <SheetTitle>Record Payment</SheetTitle>
              <SheetDescription>
                {selectedMembership && players.find(p => p.user_id === selectedMembership.user_id)?.name}
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-4 py-6">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Any notes..."
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
              </div>
              <Button className="w-full bg-green-500 hover:bg-green-600" onClick={handleRecordPayment} data-testid="confirm-payment-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm Payment
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
